import { ApolloProvider } from "@apollo/client/react";
import { useIdToken } from "@sans-service-tier/sdk-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { createClient } from "../../apollo/client";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { Sans } from "../../util/sdk";

export function AuthenticatedApolloProvider({
  requiredRole,
  children,
}: Readonly<{
  requiredRole?: string;
  children: React.ReactElement;
}>): React.ReactElement | null {
  const [authorized, setAuthorized] = useState<boolean>();
  const [isInitializing, setIsInitializing] = useState(true);
  const { loading, token: authToken } = useIdToken();

  const navigate = useNavigate();
  const { reportError } = useErrorHandler();

  // Create Apollo client once and store in ref to prevent recreation
  const clientRef = useRef<ReturnType<typeof createClient> | null>(null);
  const reportErrorRef = useRef(reportError);

  // Update the error handler ref when it changes
  reportErrorRef.current = reportError;

  // Create client only once
  const client = useMemo(() => {
    // Create client with a stable error handler that uses the current ref
    clientRef.current ??= createClient((severity, message, errorMeta) => {
      reportErrorRef.current(severity, message, errorMeta);
    });
    return clientRef.current;
  }, []); // Empty dependency array - client created only once

  async function authorizeRole(role: string) {
    setAuthorized(await Sans.sdk.isAuthorized(role));
  }

  useEffect(() => {
    if (!loading && !authToken) {
      const redirectPath = location.pathname;
      navigate("/authorize", { state: { redirectPath } });
    } else if (!loading && authToken && requiredRole) {
      authorizeRole(requiredRole);
    } else if (!loading && authToken && !requiredRole) {
      setAuthorized(true);
    }
  }, [loading, authToken, navigate, requiredRole]);

  useEffect(() => {
    // Set initializing to false after a short delay to allow auth to stabilize
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Show loading during auth initialization
  if (loading || isInitializing || !client) {
    return null; // Let the app-level loading screen handle this
  }

  // If no auth token, redirect will happen in useEffect
  if (!authToken) {
    return null;
  }

  // If authorization is still being checked
  if (authorized === undefined) {
    return null; // Brief loading state while checking authorization
  }

  // If not authorized for required role
  if (!authorized) {
    return <div>Unauthorized</div>;
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
